import { store } from '../store';
import { api } from '../http';
import { DefaultRes } from '../types';
import { AxiosResponse } from 'axios';
import { MyDashboardTypes } from '../types/my-dashboard.types';
import { AlertTypes } from '../types/alert.types';
import type { MyDashboardScope } from '../store/my-dashboard.store';

interface DrillDownResponse {
    items: MyDashboardTypes.DrillDownRecord[];
    page: number;
    perPage: number;
    total: number;
}

interface ActiveEventsResponse {
    items: AlertTypes.Model[];
}

export class MyDashboardService {
    public static readonly dispatch = store.dispatch.myDashboard;

    /**
     * Fetch summary counts for all three zones.
     */
    static async fetchSummary() {
        const { toggleLoading, setSummary, setSummaryError } = MyDashboardService.dispatch;
        toggleLoading('summary', true);
        setSummaryError(false);
        try {
            const { data: { result } } = await api.get<
                DefaultRes<MyDashboardTypes.Summary>,
                AxiosResponse<DefaultRes<MyDashboardTypes.Summary>>
            >('my-dashboard/summary');
            setSummary(result);
        } catch (err) {
            console.error('[MyDashboard] fetchSummary failed', (err as any)?.message);
            setSummaryError(true);
        } finally {
            toggleLoading('summary', false);
        }
    }

    /**
     * Fetch the current user's scope (role + groups/teams they lead)
     * for the ScopeBanner.
     */
    static async fetchScope() {
        const { toggleLoading, setScope, setScopeError } = MyDashboardService.dispatch;
        toggleLoading('scope', true);
        setScopeError(false);
        try {
            const { data: { result } } = await api.get<
                DefaultRes<MyDashboardScope>,
                AxiosResponse<DefaultRes<MyDashboardScope>>
            >('my-dashboard/scope');
            setScope(result);
        } catch (err) {
            // Surface the error so the banner can distinguish "not configured"
            // (scope stays null, no error) from "network failed" (scope null +
            // scopeError). GROUP_LEADER/TEAM_LEADER users otherwise see a
            // silently missing banner with no recovery path.
            console.error('[MyDashboard] fetchScope failed', (err as any)?.message);
            setScopeError(true);
        } finally {
            toggleLoading('scope', false);
        }
    }

    /**
     * Fetch live "Happening Now" events. Pass silent=true for background polls
     * so the table doesn't flash its loading state on every refresh.
     *
     * Returns `true` on success, `false` on failure, so the caller's polling
     * loop can back off after consecutive failures.
     */
    static async fetchActiveEvents(silent = false): Promise<boolean> {
        const {
            toggleLoading, setActiveEvents, setActiveEventsLastFetch, setActiveEventsError,
        } = MyDashboardService.dispatch;
        if (!silent) toggleLoading('activeEvents', true);
        try {
            // Server-side enforced cap; the request param is explicit here so
            // the payload contract is self-documenting on both sides of the
            // wire and reviewers don't need to dig into the backend to confirm
            // this poll is bounded. Backend ignores values above its own cap.
            const { data: { result } } = await api.get<
                DefaultRes<ActiveEventsResponse>,
                AxiosResponse<DefaultRes<ActiveEventsResponse>>
            >('my-dashboard/active-events', { params: { limit: 50 } });
            setActiveEvents(result.items || []);
            setActiveEventsLastFetch(Date.now());
            setActiveEventsError(false);
            return true;
        } catch (err) {
            // Leave the prior list in place on transient errors. Set the
            // error flag so the zone can surface a first-load failure as
            // an error state (distinct from "no events" when lastFetch=0).
            console.error('[MyDashboard] fetchActiveEvents failed', (err as any)?.message);
            setActiveEventsError(true);
            return false;
        } finally {
            if (!silent) toggleLoading('activeEvents', false);
        }
    }

    /**
     * Fetch drill-down records for a given metric.
     */
    static async fetchDrillDown(
        metricKey: MyDashboardTypes.MetricKey,
        page = 0,
        perPage = 25
    ) {
        const { toggleLoading, setDrillDown, setDrillDownError } = MyDashboardService.dispatch;
        toggleLoading('drillDown', true);
        setDrillDownError(false);
        try {
            const { data: { result } } = await api.get<
                DefaultRes<DrillDownResponse>,
                AxiosResponse<DefaultRes<DrillDownResponse>>
            >('my-dashboard/drill-down', {
                params: { metric: metricKey, page, perPage },
            });
            setDrillDown({
                metricKey,
                items: result.items,
                page: result.page,
                perPage: result.perPage,
                total: result.total,
            });
        } catch (err) {
            console.error('[MyDashboard] fetchDrillDown failed', (err as any)?.message);
            setDrillDownError(true);
        } finally {
            toggleLoading('drillDown', false);
        }
    }
}
